import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FileUploadProps {
  visaApplicationId: string;
  onUploadComplete: () => void;
  category?: string;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const DOCUMENT_CATEGORIES = [
  { value: 'passport', label: 'Pasaport' },
  { value: 'photo', label: 'Fotoğraf' },
  { value: 'financial', label: 'Mali Belgeler' },
  { value: 'academic', label: 'Akademik Belgeler' },
  { value: 'accommodation', label: 'Konaklama Belgesi' },
  { value: 'insurance', label: 'Sağlık Sigortası' },
  { value: 'flight', label: 'Uçak Bileti' },
  { value: 'invitation', label: 'Davet Mektubu' },
  { value: 'other', label: 'Diğer' }
];

export function FileUpload({ visaApplicationId, onUploadComplete, category }: FileUploadProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(category || 'other');
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newFiles: UploadingFile[] = files.map(file => ({
      file,
      progress: 0,
      status: 'uploading'
    }));

    setUploadingFiles(prev => [...prev, ...newFiles]);

    files.forEach((file, index) => {
      uploadFile(file, newFiles.length - files.length + index);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file: File, index: number) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `visa-documents/${visaApplicationId}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('visa-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      setUploadingFiles(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], progress: 100, status: 'success' };
        return updated;
      });

      const { data: { user } } = await supabase.auth.getUser();

      const { error: dbError } = await supabase
        .from('visa_documents')
        .insert({
          visa_application_id: visaApplicationId,
          file_name: file.name,
          document_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          category: selectedCategory,
          document_type: selectedCategory,
          status: 'uploaded',
          uploaded_by: user?.id,
          uploaded_at: new Date().toISOString(),
          notes: notes || null
        });

      if (dbError) throw dbError;

      setTimeout(() => {
        setUploadingFiles(prev => prev.filter((_, i) => i !== index));
        onUploadComplete();
      }, 2000);

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadingFiles(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'error',
          error: error.message || 'Yükleme başarısız'
        };
        return updated;
      });
    }
  };

  const removeFile = (index: number) => {
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Belge Kategorisi
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            {DOCUMENT_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Notlar (Opsiyonel)
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Belge hakkında notlar..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        />

        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />

        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Dosya Seç
        </button>

        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          veya sürükle bırak
        </p>

        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          PDF, Word, JPG, PNG (Maks. 10MB)
        </p>
      </div>

      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 dark:text-white">Yükleniyor</h4>
          {uploadingFiles.map((upload, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <FileText className="w-5 h-5 text-gray-400" />

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {upload.file.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {(upload.file.size / 1024).toFixed(1)} KB
                </p>
              </div>

              {upload.status === 'uploading' && (
                <Loader className="w-5 h-5 text-blue-600 animate-spin" />
              )}

              {upload.status === 'success' && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}

              {upload.status === 'error' && (
                <>
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
